/**
 * Opening a published scene gives you back the component, not the lump.
 *
 * Export bakes live deformation into geometry and points `src` at a generated
 * `morphbake_<hash>.glb`. That file renders correctly in any glTF consumer and
 * re-edits nowhere — the morph rig is gone. `Provenance.bakedFrom` records the
 * asset it replaced, and an AUTHORING load resolves that instead.
 *
 * Two things this has to get right, and the first version got both wrong:
 *
 * **Where.** It has to run on every path that turns loaded data into a
 * SceneObject, not just one. `SceneCreation.initializeSceneObject` handles the
 * scene root, single assets and editor create/duplicate — it is NOT on the
 * path any scene FORMAT takes. Every `.trace`/`.zip`/`.tracex`/`.glxf` load
 * runs through `TraceScene.loadFromPluginResult`, which builds its own node
 * data. A restore wired only into the first covers exactly the cases that
 * never carry provenance.
 *
 * **Which signal.** `ConfigData.playMode` is derived once, from the
 * `play-mode` attribute (`TraceViewer`), defaulting to TRUE when the
 * attribute is absent. An editor that boots without that attribute and then
 * calls `setPlayMode(false)` leaves `config.playMode` true forever. Keyed on
 * it, the restore would never fire in that editor. (An editor that sets
 * `play-mode="false"` before the element connects starts in edit mode, so
 * its first load is already an authoring load.) The LIVE state is the honest
 * signal: it is what the host is actually in, and it is the same question a
 * runtime answers "yes" to.
 *
 * **Which src.** The note is true of the bake it was saved beside and of
 * nothing else. Once the src changes — a form swap, a filled placeholder —
 * the note names a form the object no longer has, and the object's data
 * carries it on into duplicates, undo snapshots and saves. So the reader acts
 * on it only beside a bake ({@link isBakedGeometrySrc}), and the export, its
 * one writer, keeps it only there (`TraceExport`).
 */

import type { BehaviorData } from "../scenedata/behaviordata";
import { Logger } from "../utils/Logger";

/** Whether a load is an authoring session, from the state the host is in. */
export interface PlayModeSource {
    isPlayMode(): boolean;
}

// The export writes three kinds of baked geometry, and their names are decided
// here, beside {@link isBakedGeometrySrc}: the name is also how a src is known
// to be a bake, so the writers and the recognizer share it or drift.
const DEFORMATION_BAKE_PREFIX = "morphbake_";
const BEVEL_BAKE_PREFIX = "bevel_";
const TEXT_BAKE_PREFIX = "text3d_";
const BAKE_EXTENSION = ".glb";

/** The file a deformation bake (MorphStretch, MeshBackground) writes, for a content key. */
export function deformationBakeFilename(contentKey: string): string {
    return `${DEFORMATION_BAKE_PREFIX}${contentKey}${BAKE_EXTENSION}`;
}

/** The file a Bevel bake writes, for the primitive it rounds and a content key. */
export function bevelBakeFilename(host: string, contentKey: string): string {
    return `${BEVEL_BAKE_PREFIX}${host}_${contentKey}${BAKE_EXTENSION}`;
}

/**
 * The `Provenance.bakedFrom` of a bake whose rig has no `src`: its geometry
 * comes from a behavior's data alone (an extruded Text's body), so an
 * authoring load opens no `src` and the behavior builds the geometry again.
 */
export const NO_SRC_RIG = "builtin:none";

/** The file an extruded Text's body bake writes, for a content key. */
export function textBodyBakeFilename(contentKey: string): string {
    return `${TEXT_BAKE_PREFIX}${contentKey}${BAKE_EXTENSION}`;
}

/** The export's geometry bakes. A `text` bake is an extruded Text's body; its rig is {@link NO_SRC_RIG}. */
export type BakeKind = "deformation" | "bevel" | "text";

const BAKE_PREFIXES: Readonly<Record<BakeKind, string>> = {
    deformation: DEFORMATION_BAKE_PREFIX,
    bevel: BEVEL_BAKE_PREFIX,
    text: TEXT_BAKE_PREFIX,
};

/**
 * Which bake wrote `src`, if one did, wherever the file now lives: bundled
 * (`./assets/…`), re-homed under a nested scene's folder, or hosted.
 *
 * The names are the export's own, not a user's, so this reads a result the
 * writer authored rather than classifying content. It is asked by the export,
 * by an authoring load, by the load-boundary scale migration
 * (`normalizeLegacyBakedSlotScale`), which must leave a bake's TRS alone, by
 * Model, which does not size a Bevel bake (a primitive's policy) or a text
 * bake (its Text measures the node) for layout,
 * and by MorphStretch at runtime for one thing: whether `src` is a deformation
 * bake, whose geometry is the Base pose. Otherwise a runtime renders `src` and
 * never asks.
 */
export function bakeKindOf(src: unknown): BakeKind | undefined {
    if (typeof src !== "string") return undefined;
    const path = src.split(/[?#]/, 1)[0];
    const file = path.slice(path.lastIndexOf("/") + 1);
    if (!file.endsWith(BAKE_EXTENSION)) return undefined;
    return (Object.keys(BAKE_PREFIXES) as BakeKind[]).find(kind => file.startsWith(BAKE_PREFIXES[kind]));
}

/** Whether `src` is a bake's output — the only src a `Provenance.bakedFrom` note speaks for. */
export function isBakedGeometrySrc(src: unknown): boolean {
    return bakeKindOf(src) !== undefined;
}

/**
 * The src an authoring load should open: `Provenance.bakedFrom` when the src
 * is a bake and the note names its rig, none when the note is
 * {@link NO_SRC_RIG}, otherwise the src as given.
 *
 * Beside any other src the note is left over from before the src changed, and
 * opening it would undo the change — a BG swapped from Inset Lip to Frame
 * Ledge, duplicated or saved, came back as Inset Lip.
 *
 * The note is read, not consumed: whether it survives is the export's call.
 */
export function authoringSrc(
    src: string | undefined,
    behaviors: readonly BehaviorData[] | readonly unknown[] | undefined,
    playMode: PlayModeSource,
    nodeName?: string,
    /** The node's uid, so the replaced bake is remembered ({@link adoptLoadedBake}). */
    uid?: string,
): string | undefined {
    if (playMode.isPlayMode()) return src;
    const authored = bakedFromRig(src, behaviors);
    if (authored === undefined) return src;
    if (authored === "") {
        Logger.debug(`[restoreBakedSrc] "${nodeName ?? "?"}": authoring load, its behaviors build what was baked as ${String(src)}`);
        return undefined;
    }
    if (uid !== undefined && src) {
        let replaced = REPLACED_BAKES.get(playMode);
        if (!replaced) REPLACED_BAKES.set(playMode, replaced = new Map<string, string>());
        replaced.set(uid, src);
    }
    Logger.debug(`[restoreBakedSrc] "${nodeName ?? "?"}": authoring load, opening ${authored} instead of the baked ${String(src)}`);
    return authored;
}

/**
 * The rig an authoring load would open in place of `src`, `""` when it opens
 * no src ({@link NO_SRC_RIG}), or `undefined` when it would keep `src`: the
 * {@link authoringSrc} rule without the play-mode half.
 *
 * A loader that instantiates files itself asks this to know that a file may
 * be replaced before the node is built — a bake it loaded would sit beside
 * the rig the authoring load builds instead (see `GltfSceneLoaderPlugin`).
 */
export function bakedFromRig(
    src: unknown,
    behaviors: readonly BehaviorData[] | readonly unknown[] | undefined,
): string | undefined {
    if (!isBakedGeometrySrc(src)) return undefined;
    const authored = provenanceOf(behaviors)?.bakedFrom;
    if (typeof authored !== "string" || !authored || authored === src) return undefined;
    return authored === NO_SRC_RIG ? "" : authored;
}

/**
 * What an authoring session knows about an object's bake, at runtime only.
 *
 * "If we haven't modified it, we're not responsible for putting it back": an
 * authoring load opens the rig in place of the bake ({@link authoringSrc})
 * and remembers the bake ({@link adoptLoadedBake}). Until something that
 * shapes that bake changes ({@link markBakeDirty}), a folder save names the
 * remembered bake instead of making it again. A bake a save writes takes
 * over once it is stored.
 */
interface BakeState {
    /** Bumped by every {@link markBakeDirty}. */
    generation: number;
    /**
     * The bake to name, true of `rig` at `generation`. `stored` is the
     * authored uri of a bake this session stored, which must still be on
     * record for `src` to be named.
     */
    current?: { src: string; rig: string; kinds: string; generation: number; stored?: string };
    /** The last bake an export wrote, by its authored uri, before it is stored. */
    written?: { uri: string; rig: string; kinds: string; generation: number };
}

const BAKE_STATES = new WeakMap<object, BakeState>();

/** Per session, node uid → the bake src an authoring load replaced with its rig. */
const REPLACED_BAKES = new WeakMap<PlayModeSource, Map<string, string>>();

function bakeState(object: object): BakeState {
    let state = BAKE_STATES.get(object);
    if (!state) {
        state = { generation: 0 };
        BAKE_STATES.set(object, state);
    }
    return state;
}

/**
 * After an object's behaviors are built: if its load swapped a bake for the
 * rig it now shows, remember that bake. Read once, so a rebuild of the same
 * object does not reset what it knows.
 */
export function adoptLoadedBake(object: BakedObject & { uid?: string }, session: PlayModeSource): void {
    const replaced = object.uid === undefined ? undefined : REPLACED_BAKES.get(session);
    const bake = object.uid === undefined ? undefined : replaced?.get(object.uid);
    if (!bake || typeof object.src !== "string") return;
    replaced!.delete(object.uid!);
    const state = bakeState(object);
    state.current = { src: bake, rig: object.src, kinds: behaviorKinds(object), generation: state.generation };
    state.written = undefined;
}

/**
 * Something that shapes `object`'s bake changed. Called from the funnels
 * those inputs pass through; marking too often costs a re-bake, missing a
 * mark ships a stale one.
 */
export function markBakeDirty(object: object): void {
    const state = BAKE_STATES.get(object);
    if (state) state.generation++;
}

/** What a bake was made from, taken before it starts so a change during it is not lost. */
export interface BakeStamp {
    rig: string;
    kinds: string;
    generation: number;
}

export function bakeStamp(object: BakedObject): BakeStamp {
    return { rig: object.src ?? "", kinds: behaviorKinds(object), generation: bakeState(object).generation };
}

/** An export wrote `uri` as `object`'s bake, made as of `stamp`. */
export function recordWrittenBake(object: BakedObject, uri: string, stamp: BakeStamp): void {
    bakeState(object).written = { uri, ...stamp };
}

/** The bake `object` would replace, whether or not it is still current. */
export function rememberedBake(object: BakedObject): string | undefined {
    const current = BAKE_STATES.get(object)?.current;
    return current && current.rig === object.src ? current.src : undefined;
}

/** A re-bake came out identical to {@link rememberedBake}: it is current as of `stamp`. */
export function keepRememberedBake(object: BakedObject, stamp: BakeStamp): void {
    const state = BAKE_STATES.get(object);
    if (state?.current) state.current = { ...state.current, kinds: stamp.kinds, generation: stamp.generation };
}

/**
 * The bake an export may name for `object` instead of baking: the
 * remembered one, when nothing marked it dirty since and `src` is still the
 * rig it was made from. `storedUri` answers where this export's folder
 * stored a written bake; one stored since the last export becomes the
 * remembered bake, and stays it only while that record stands.
 */
export function unchangedBake(
    object: BakedObject,
    storedUri: (authoredUri: string) => string | undefined,
): string | undefined {
    const state = BAKE_STATES.get(object);
    if (!state) return undefined;
    const written = state.written;
    const stored = written && storedUri(written.uri);
    if (written && stored) {
        state.current = { src: stored, rig: written.rig, kinds: written.kinds, generation: written.generation, stored: written.uri };
        state.written = undefined;
    }
    const current = state.current;
    // A behavior added or removed since is a change no commit reports.
    if (!current || current.rig !== object.src || current.kinds !== behaviorKinds(object)) return undefined;
    if (current.stored !== undefined && storedUri(current.stored) !== current.src) {
        // The record is gone (forgotten, or another folder): the folder may
        // not hold it, so it is neither named nor compared against.
        state.current = undefined;
        return undefined;
    }
    return current.generation === state.generation ? current.src : undefined;
}

/** What the bake state reads off an object: its src and its behaviors. */
interface BakedObject {
    src?: string;
    getBehaviors(): ReadonlyArray<{ name: string }>;
}

function behaviorKinds(object: BakedObject): string {
    return object.getBehaviors().map(behavior => behavior.name).join(",");
}

function provenanceOf(behaviors: readonly unknown[] | undefined): { bakedFrom?: unknown } | undefined {
    return (behaviors ?? []).find(
        (b): b is { bakedFrom?: unknown } =>
            !!b && typeof b === "object" && (b as { "@type"?: unknown })["@type"] === "Provenance",
    );
}
